import { isValid, parseISO } from 'date-fns';
import sortBy from 'lodash/sortBy';

import {
  DemandLineItemTypes,
  LineItemType,
  UsageLineItemTypes,
} from 'common/src/types/enums/LineItemType.enum';
import {
  ServiceDemandUnits,
  ServiceLineItemTypes,
  ServiceUsageUnits,
} from 'common/src/types/enums/Service.enum';
import {
  ILineItem,
  ILineItemSubtype,
  IMeter,
} from 'common/src/types/interfaces';

import { getStartAndEndDateFields } from 'portal/src/components/common/forms/commonFormFields';

import {
  autoCompleteType,
  datePickerType,
  numberType,
  selectInputType,
  textInputType,
} from '../../../../common/forms/FormFieldTypes';

export type LineItemFormFields = {
  meterId: string;
  facilityName: string;
  type: LineItemType;
  subTypeId: string;
  description: string;
  unitOfMeasure: string;
  startDate: Date | null;
  endDate: Date | null;
  usage: number;
  cost: number;
  meterCurrentReading: number | null;
  meterPreviousReading: number | null;
  meterReadingMultiplier: number | null;
};

export const defaultValues = (
  entity: Partial<ILineItem> = {},
  defaultEndDate: Date,
  defaultStartDate: Date,
  meters: Partial<IMeter>[] = [],
): LineItemFormFields => {
  const getDate = (date: string | Date | null, defaultDate: Date) => {
    if (date && typeof date === 'string') {
      return parseISO(date);
    } else if (date) {
      return date;
    }
    return isValid(defaultDate) ? defaultDate : null;
  };

  return {
    meterId: entity.meterId ?? '',
    facilityName:
      meters.find(({ id }) => id === entity.meterId)?.parentFacility?.name ??
      entity.meter?.parentFacility?.name ??
      '',
    type: entity.type ?? '',
    subTypeId: entity.subTypeId ?? '',
    description: entity.description ?? '',
    unitOfMeasure: entity.unitOfMeasure ?? '',
    startDate: getDate(entity.startDate, defaultStartDate),
    endDate: getDate(entity.endDate, defaultEndDate),
    usage: entity.usage ?? 0,
    cost: entity.cost ?? 0,
    meterCurrentReading: entity.meterCurrentReading ?? null,
    meterPreviousReading: entity.meterPreviousReading ?? null,

    meterReadingMultiplier: entity.meterReadingMultiplier ?? null,
  } as any;
};

export const mapUnitOfMeasureValues = (
  lineItem: Partial<ILineItem>,
  meters: Partial<IMeter>[],
) => {
  if (!lineItem?.meterId) return [];

  const serviceType = meters.find(
    (meter) => meter.id === lineItem.meterId,
  )?.serviceType;

  if (serviceType) {
    const demandUnits = ServiceDemandUnits[serviceType];
    const usageUnits = ServiceUsageUnits[serviceType];
    const createUnitOptions = (units: string[] = []) =>
      units.map((unit) => ({
        label: unit,
        value: unit,
      }));

    if (DemandLineItemTypes.includes(lineItem.type)) {
      return createUnitOptions(demandUnits ?? []);
    } else if (UsageLineItemTypes.includes(lineItem.type)) {
      return createUnitOptions(usageUnits ?? []);
    } else {
      return [
        ...(demandUnits?.length ? createUnitOptions(demandUnits) : []),
        ...(usageUnits?.length ? createUnitOptions(usageUnits) : []),
      ];
    }
  }
  return [];
};

export const getFields = (
  lineItemSubTypes: ILineItemSubtype[],
  meters: Partial<IMeter>[],
  selectedMeterId: string,
  selectedType: LineItemType,
  onMeterChange: (meterId: string) => void,
) => {
  const mapLineItemTypes = () => {
    if (!selectedMeterId) return [];

    const selectedMeter = meters.find((meter) => meter.id === selectedMeterId);

    if (!selectedMeter) return [];

    const { serviceType } = selectedMeter;

    if (serviceType) {
      const lineItemTypes = [...ServiceLineItemTypes[serviceType]];

      const itemTypes = Object.keys(lineItemTypes.sort()).map((key) => ({
        label: lineItemTypes[key],
        value: lineItemTypes[key],
      }));

      return itemTypes;
    }
    return [];
  };

  const formFieldsConfiguration: Array<
    | datePickerType
    | numberType
    | selectInputType
    | textInputType
    | autoCompleteType
  > = [
    ...getStartAndEndDateFields({ required: true }),
    {
      label: 'Meter Name',
      name: 'meterId',
      placeholder: 'Meter Name',
      type: 'selectInput',
      required: true,
      onChange: onMeterChange,
      values: sortBy(meters, (m) => m.name.toLowerCase()).map((meter) => ({
        label:
          meter.name + `${meter.serviceType ? ` (${meter.serviceType})` : ''}`,
        value: meter.id,
      })),
    },
    {
      label: 'Facility Name',
      name: 'facilityName',
      placeholder: 'Facility Name',
      type: 'textInput',
      required: true,
      disabled: true,
    },
    {
      label: 'Line Item Type',
      name: 'type',
      placeholder: 'Line Item Type',
      type: 'selectInput',
      required: true,
      values: mapLineItemTypes(),
      disabled: !selectedMeterId,
    },
    {
      label: 'Unit',
      name: 'unitOfMeasure',
      placeholder: 'Unit',
      type: 'selectInput',
      required: UsageLineItemTypes.includes(selectedType),
      disabled: !selectedType,
      values: mapUnitOfMeasureValues(
        {
          meterId: selectedMeterId,
          type: selectedType,
        },
        meters,
      ),
    },
    {
      label: 'Line Item Subtype',
      name: 'subTypeId',
      placeholder: 'Line Item Subtype',
      type: 'autocomplete',
      required: false,
      disabled: !selectedType,
      options: sortBy(lineItemSubTypes, (li_st) =>
        li_st.name.toLowerCase(),
      ).map((lineItemSubType) => ({
        label: lineItemSubType.name,
        value: lineItemSubType.id,
      })),
    },
    {
      label: 'Line Item Description',
      name: 'description',
      placeholder: 'Line Item Description',
      type: 'textInput',
      helperText: 'Max 100 characters',
      maxLength: 100,
      required: false,
      disabled: !selectedType,
    },
    {
      allowNegative: true,
      label: 'Usage',
      name: 'usage',
      placeholder: 'Usage',
      type: 'number',
      required: false,
    },
    {
      label: 'Cost',
      name: 'cost',
      placeholder: 'Cost',
      type: 'number',
      required: false,
      adorment: '$ ',
      allowNegative: true,
      autoDecimalFormat: true,
    },
  ];

  return formFieldsConfiguration;
};
